import {
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { createPortal } from 'react-dom';

export interface FullScreenModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subTitle?: string;
  breadcrumbs?: ReactNode;
  children: ReactNode;
  sidebar?: ReactNode;
  footer?: ReactNode;
  allowBackdropClose?: boolean;
  closeLabel?: string;
  initialFocusRef?: React.RefObject<HTMLElement>;
  headerActions?: ReactNode;
  disableFocusTrap?: boolean;
  closeButton?: ReactNode;
}

const DEFAULT_CLOSE_LABEL = 'Close modal';

export const FullScreenModal = ({
  open,
  onClose,
  title,
  subTitle,
  breadcrumbs,
  children,
  sidebar,
  footer,
  allowBackdropClose = false,
  closeLabel = DEFAULT_CLOSE_LABEL,
  initialFocusRef,
  headerActions,
  disableFocusTrap = false,
  closeButton,
}: FullScreenModalProps) => {
  const [portalElement] = useState<HTMLElement | null>(() =>
    typeof document === 'undefined' ? null : document.createElement('div'),
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  const getFocusableElements = useCallback((): HTMLElement[] => {
    if (!containerRef.current) return [];
    return Array.from(
      containerRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => !el.closest('[aria-hidden="true"]'));
  }, []);

  useEffect(() => {
    if (!portalElement || typeof document === 'undefined') {
      return undefined;
    }

    portalElement.setAttribute('data-quip-modal-root', 'true');
    document.body.appendChild(portalElement);

    return () => {
      portalElement.remove();
    };
  }, [portalElement]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') {
      return undefined;
    }

    previouslyFocusedElementRef.current = document.activeElement as HTMLElement | null;

    // Set initial focus on the modal container (or initialFocusRef if provided)
    const frame = requestAnimationFrame(() => {
      if (disableFocusTrap) return;
      const target = initialFocusRef?.current ?? containerRef.current;
      target?.focus();
    });

    return () => {
      cancelAnimationFrame(frame);
      previouslyFocusedElementRef.current?.focus?.();
      previouslyFocusedElementRef.current = null;
    };
  }, [open, disableFocusTrap, initialFocusRef]);

  useEffect(() => {
    if (!open || typeof document === 'undefined' || typeof window === 'undefined') {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';

    if (scrollBarWidth > 0) {
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, [open]);

  useEffect(() => {
    if (!open || typeof window === 'undefined') {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || disableFocusTrap) return;

      const focusable = getFocusableElements();
      if (focusable.length === 0) return;

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || active === containerRef.current) {
          event.preventDefault();
          last?.focus();
        }
      } else {
        if (active === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose, disableFocusTrap, getFocusableElements]);

  if (!portalElement || !open) {
    return null;
  }

  const handleBackdropMouseDown = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    if (allowBackdropClose) {
      event.preventDefault();
      onClose();
    }
  };

  const ariaDescribedBy = subTitle ? descriptionId : undefined;

  const content = (
    <Box
      role="presentation"
      sx={(theme) => ({
        position: 'fixed',
        inset: 0,
        zIndex: theme.zIndex.modal,
        display: 'flex',
        backgroundColor: alpha(theme.palette.common.black, 0.6),
      })}
      onMouseDown={handleBackdropMouseDown}
    >
      <Box
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={ariaDescribedBy}
        tabIndex={-1}
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          width: '100vw',
          height: '100vh',
          backgroundColor: theme.palette.background.default,
          boxShadow: theme.shadows[24],
        })}
      >
        <Box
          component="header"
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(2),
            minHeight: 64,
            padding: theme.spacing(2, 3),
            [theme.breakpoints.down('md')]: {
              alignItems: 'flex-start',
              flexDirection: 'column',
              padding: theme.spacing(2),
            },
          })}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {breadcrumbs}
            <Typography id={titleId} variant="h5" component="h2">
              {title}
            </Typography>
            {subTitle ? (
              <Typography id={descriptionId} variant="body2" color="text.secondary">
                {subTitle}
              </Typography>
            ) : null}
          </Box>
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(1.5),
              alignSelf: { xs: 'stretch', md: 'center' },
            })}
          >
            {headerActions}
            {closeButton ?? (
              <IconButton aria-label={closeLabel} edge="end" onClick={onClose} size="large">
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        <Divider />

        <Box
          sx={(theme) => ({
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            backgroundColor: theme.palette.background.paper,
          })}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              minHeight: 0,
            }}
          >
            <Box
              component="main"
              sx={(theme) => ({
                flex: 1,
                overflowY: 'auto',
                padding: theme.spacing(3),
                [theme.breakpoints.down('md')]: {
                  padding: theme.spacing(2),
                },
              })}
            >
              {children}
            </Box>
            {sidebar ? (
              <Box
                sx={(theme) => ({
                  display: 'flex',
                  flexDirection: 'column',
                  width: { xs: '100%', md: 360 },
                  flexShrink: 0,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  [theme.breakpoints.up('md')]: {
                    borderTop: 'none',
                    borderLeft: `1px solid ${theme.palette.divider}`,
                    maxWidth: 420,
                  },
                })}
              >
                <Box
                  component="aside"
                  sx={(theme) => ({
                    flex: 1,
                    overflowY: 'auto',
                    padding: theme.spacing(3),
                    [theme.breakpoints.down('md')]: {
                      padding: theme.spacing(2),
                    },
                  })}
                >
                  {sidebar}
                </Box>
                {footer ? (
                  <>
                    <Divider />
                    <Box
                      component="footer"
                      sx={(theme) => ({
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                        gap: theme.spacing(1.5),
                        padding: theme.spacing(2, 3),
                        paddingBottom: `calc(${theme.spacing(2)} + env(safe-area-inset-bottom, 0px))`,
                        [theme.breakpoints.down('md')]: {
                          padding: theme.spacing(2),
                          paddingBottom: `calc(${theme.spacing(2)} + env(safe-area-inset-bottom, 0px))`,
                        },
                      })}
                    >
                      {footer}
                    </Box>
                  </>
                ) : null}
              </Box>
            ) : null}
          </Box>
        </Box>

        {!sidebar && footer ? (
          <>
            <Divider />
            <Box
              component="footer"
              sx={(theme) => ({
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
                gap: theme.spacing(1.5),
                padding: theme.spacing(2, 3),
                paddingBottom: `calc(${theme.spacing(2)} + env(safe-area-inset-bottom, 0px))`,
                [theme.breakpoints.down('md')]: {
                  padding: theme.spacing(2),
                  paddingBottom: `calc(${theme.spacing(2)} + env(safe-area-inset-bottom, 0px))`,
                },
                backgroundColor: theme.palette.background.paper,
              })}
            >
              {footer}
            </Box>
          </>
        ) : null}
      </Box>
    </Box>
  );

  return createPortal(content, portalElement);
};
